<template>
  <base-invite
    :errors="errors"
    :title="title"
    :primaryLoading="primaryLoading"
  >
    <template v-slot:invite-header>
      <h3>
        {{$t('organizations.users_section.modals.invite_users.copy_paste_emails')}}
      </h3>
    </template>
    <template v-slot:invite-body>
      <el-form
        ref="addUserForm"
        :model="form"
        :rules="rules"
        class="email-invite-form"
      >
        <el-form-item
          :label="$t('organizations.users_section.modals.invite_users.copy_and_paste')"
          prop="newUsersEmails"
          class="elsa-input"
          show-message
          name="email"
          allow-edit-tags="true"
        >
          <vue-tags-input
            v-model="helpers.newUserEmail"
            class="elsa-input elsa-tags-input"
            :tags="helpers.newUsersEmails"
            :add-on-blur="true"
            :placeholder="$t('organizations.users_section.modals.invite_users.add_email')"
            :validation="validations"
            :add-from-paste="true"
            :separators="[ '\n', '\r\n', ';', ',', ' ']"
            @tags-changed="addUsersEmails"
          />
        </el-form-item>

        <elsa-select
          name="selectedRole"
          v-if="selectRolesAvailable"
          v-model.lazy="form.selectedRole"
          :label="$t('organizations.users_section.modals.invite_users.select_role_label')"
          :placeholder="$t('organizations.users_section.modals.invite_users.select_role_placeholder')"
          :options="availableRoles"
          class="invite-email-select-role"
        />

      </el-form>
    </template>
    <template v-slot:invite-footer>
      <primary-button
        disabled
        v-loading="primaryLoading"
        ref="submitButton"
        @click.native="handleConfirmInvite">
        {{$t('common.invite')}}
      </primary-button>
      <tertiary-button
        @click.native="backToUsers"
      >
        {{$t('common.cancel')}}
      </tertiary-button>
    </template>
  </base-invite>
</template>

<script>
  import BaseInvite from './BaseInvite'
  import PrimaryButton from '@/components/common/buttons/PrimaryButton'
  import TertiaryButton from '@/components/common/buttons/TertiaryButton'
  import VueTagsInput from '@johmun/vue-tags-input';
  import ElsaSelect from '@/components/common/Select'
  import { handleFormValidation, handleSubmitButtonState } from '../../../helpers/formValidationHelper';
  import { pluck, compose, complement, isEmpty, pathOr, both, propEq, reject, path, defaultTo } from 'ramda';
  import { selectUserRoles, ADMIN } from '@/constants/userRoles'

  export default {
    components: {
      BaseInvite,
      PrimaryButton,
      VueTagsInput,
      TertiaryButton,
      ElsaSelect,
    },
    props: {
      organizationData: {
        type: Object,
        default: () => {}
      },
      inviteUsers: {
        type: Function,
        default: () => {}
      },
      selectRolesAvailable: {
        type: Boolean,
        default: true
      },
      defaultRole: {
        type: String,
        default: ''
      },
      title: {
        Type: String,
      }
    },
    data() {
      return {
        primaryLoading: false,
        errors: {},
        helpers: {
          newUserEmail:'',
          newUsersEmails: [],
        },
        form: {
          selectedRole: this.defaultRole,
        },
        validations: [{
            classes: 'no-email',
            rule: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            disableAdd: true,
        }],
        rules: {
          newUsersEmails: [
            {
              type: 'array',
              required: true,
              trigger: ['change', 'blur'],
              defaultField: {
                type: "email",
                message: this.$t('organizations.users_section.modals.invite_users.email_validate_message'),
              }
            }
          ],
          selectedRole: [
            {
              type: "string",
              required: true,
              message:this.$t('organizations.users_section.modals.invite_users.select_role_validate_msg'),
              trigger: ['change', 'blur'],
            }
          ]
        }
      }
    },
    computed: {
      availableRoles () {
        return reject(propEq('value', ADMIN), selectUserRoles);
      },
      organizationName() {
        const organizationName = compose(
          defaultTo(this.$store.getters['organization/currentOrganization'].name),
          path([ 'organizationData', 'name' ]),
          )(this)

        return organizationName
      }
    },
    watch: {
      form: {
        deep: true,
        handler(form) {
          const buttonRef = this.$refs.submitButton.$el
          const isValid = both(
            compose(
              complement(isEmpty),
              pathOr([], ['selectedRole'])
            ),
            compose(
              complement(isEmpty),
              pathOr([], ['newUsersEmails'])
            )
          )(form)

          return handleSubmitButtonState(buttonRef, isValid)
        },
      }
    },
    methods: {
      handleSubmitInviteUsers () {
        const form = this.$refs.addUserForm
        return handleFormValidation(form, this.addUsers, this.showError)
      },
      showError (error) {
        return error;
      },
      addUsersEmails(usersEmails) {
        this.form = {
          ...this.form,
          newUsersEmails: pluck('text')(usersEmails),
        }
      },
      handleConfirmInvite(){
        this.addUsers();
      },
      async addUsers() {
        this.primaryLoading = true
        const { newUsersEmails, selectedRole: role = 'student'} = this.form;
        try {
          await this.inviteUsers(newUsersEmails, role)
          this.primaryLoading = false
          this.displaySuccess()
        } catch(error) {
          this.primaryLoading = false
          this.errors = error
        }
      },
      backToUsers(event = {}) {
        this.$emit('toggle-organization-nav', true)
        this.$emit('invite-users', 'users')
        this.$emit('closed', event.invited)
      },
      displaySuccess () {
        this.$message({
          message: this.$t('organizations.users_section.modals.invite_users.success'),
          center: true,
          customClass: 'elsa-message full-width success',
          offset: 105,
          type: 'success',
          iconClass: 'elsa-icon-check-white',
        })

        return this.backToUsers({invited: true});
      },
    },
  }
</script>

<style lang="scss" scoped>
@import '../../../scss/colors';

.email-invite-form > .el-form-item {
  padding-top: 16px;
  padding-bottom: 16px;


  &:first-child {
  padding-top: 0;
  }

  &:last-child {
  padding-bottom: 0;
  }
}

.invite-email-select-role {
  max-width: 320px;
}

.elsa-tags-input {
  border: 2px solid $purple;
  box-sizing: border-box;
  min-height: 124px;
  max-width: none;

  &::v-deep .ti-input {
    border: none;
    font-family: 'SF Pro Display';

    .ti-tag {
      font-weight: normal;
      font-size: 12px;
      color: $darker-blue;
      background: #FFFFFF;
      border: 0.5px solid $light-blue;
      box-sizing: border-box;
      height: 24px;

      span {
        padding: 0 8px 0 8px;
      }

      .ti-actions {
        margin: 0px;
        color: #546873;
      }

      &.ti-invalid {
        color: red;
      }
    }
  }
}
</style>
